import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../environments/environment';
import { AcknowledgeDialogComponent } from '../acknowledge-dialog/acknowledge-dialog.component';

@Component({
  selector: 'app-license',
  templateUrl: './license.component.html',
  styleUrls: ['./license.component.scss']
})
export class LicenseComponent implements OnInit {
  readonly defaultText = 'Paste activation code here';
  activationCode: string = this.defaultText;
  activating = false;
  loading = false;
  items: Array<{ productId: string; deactivationDate?: string; }> = [];
  displayItems: Array<{ name: string; through: string }> = [];

  constructor(private http: HttpClient, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.loadMyLicenses();
  }

  get isActivateDisabled(): boolean {
    const v = (this.activationCode || '').trim();
    return this.activating || v.length === 0 || v === this.defaultText;
  }

  onActivate(): void {
    const code = (this.activationCode || '').trim();
    if (!code || code === this.defaultText) { return; }
    this.activating = true;
    this.http.post(`${environment.apiBaseUrl}/license/activate`, null, {
      params: { 'license-key': code },
      observe: 'response',
      responseType: 'json'
    }).subscribe({
      next: (resp: HttpResponse<any>) => {
        const body = (resp.body && (resp.body.message || resp.body.error)) || '';
        this.dialog.open(AcknowledgeDialogComponent, { data: body });
        this.activating = false;
        this.loadMyLicenses();
      },
      error: (err) => {
        const body = err?.error?.error || err?.message || '';
        this.dialog.open(AcknowledgeDialogComponent, { data: body });
        this.activating = false;
      }
    });
  }

  clearDefaultMessage(): void {
    if ((this.activationCode || '') === this.defaultText) {
      this.activationCode = '';
    }
  }

  loadMyLicenses(): void {
    this.loading = true;
    this.http.get<any[]>(`${environment.apiBaseUrl}/license/mylicenses`).subscribe({
      next: (arr) => {
        const list = Array.isArray(arr) ? arr : [];
        // sort by deactivationDate desc
        this.items = list.sort((a, b) => {
          const ta = a?.deactivationDate ? new Date(a.deactivationDate).getTime() : 0;
          const tb = b?.deactivationDate ? new Date(b.deactivationDate).getTime() : 0;
          return tb - ta;
        });
        this.composeDisplayItems();
        this.loading = false;
      },
      error: (err) => {
        const body = err?.error?.error || err?.message || '';
        this.dialog.open(AcknowledgeDialogComponent, { data: body });
        this.items = [];
        this.composeDisplayItems();
        this.loading = false;
      }
    });
  }

  mapName(pid: string): string {
    const k = (pid || '').toLowerCase();
    if (k === 'ntrip-pro') return 'GeoAstra NTRIP Caster - Pro Edition';
    if (k === 'ntrip-basic') return 'GeoAstra NTRIP Caster - Basic Edition';
    if (k === 'ntrip-ent') return 'GeoAstra NTRIP Caster - Enterprise Edition';
    if (k === 'analyzer') return 'GeoAstra RTK Base Analyzer';
    if (k === 'downloader') return 'GeoAstra PPK Downloader';
    if (k === 'demo') return 'GeoAstra Demo License';
    return pid || '';
  }

  openInfo(message: string): void {
    this.dialog.open(AcknowledgeDialogComponent, { data: message });
  }

  private composeDisplayItems(): void {
    const rows: Array<{ name: string; through: string }> = [];

    // detect Pro/Basic licenses
    const pro = this.items.find(x => (x.productId || '').toLowerCase() === 'ntrip-pro');
    const hasPro = !!(pro && pro.deactivationDate);
    const basic = this.items.find(x => (x.productId || '').toLowerCase() === 'ntrip-basic');
    const hasBasic = !!(basic && basic.deactivationDate);

    // include Free Edition only when neither Pro nor Basic exists
    if (!hasPro && !hasBasic) {
      rows.push({ name: 'GeoAstra NTRIP Caster - Free Edition', through: '2099-12-31' });
    }

    // copy existing mapped licenses
    for (const it of this.items) {
      const name = this.mapName(it.productId);
      const through = it.deactivationDate ? it.deactivationDate : '';
      if (name) {
        rows.push({ name, through });
      }
    }

    // if Pro exists, add two basic edition entries with same through date
    if (hasPro && pro) {
      rows.push({ name: 'GeoAstra RTK Base Analyzer - Basic Edition', through: pro.deactivationDate! });
      rows.push({ name: 'GeoAstra PPK Downloader - Basic Edition', through: pro.deactivationDate! });
    } else if (hasBasic && basic) {
      // if no Pro but Basic exists, add two basic edition entries once
      rows.push({ name: 'GeoAstra RTK Base Analyzer - Basic Edition', through: basic.deactivationDate! });
      rows.push({ name: 'GeoAstra PPK Downloader - Basic Edition', through: basic.deactivationDate! });
    }

    // ordering: if Free exists (first element), keep it on top; sort the rest by date desc
    if (!hasPro && !hasBasic) {
      const others = rows.slice(1).sort((a, b) => {
        const ta = a.through ? new Date(a.through).getTime() : 0;
        const tb = b.through ? new Date(b.through).getTime() : 0;
        return tb - ta;
      });
      this.displayItems = [rows[0], ...others];
    } else {
      // no Free on top: sort all by date desc
      this.displayItems = rows.sort((a, b) => {
        const ta = a.through ? new Date(a.through).getTime() : 0;
        const tb = b.through ? new Date(b.through).getTime() : 0;
        return tb - ta;
      });
    }
  }
}


